import { useState } from 'react';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { post, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type SendResult = {
  ok?: boolean;
  error?: string;
  message?: string;
  sent?: number;
  skipped?: number;
  failed?: number;
  buyers?: { email?: string; status?: string }[];
  buyerSource?: string;
};

function errMsg(e: unknown, fallback: string): string {
  const body = (e as ApiError).body as { message?: string; error?: string } | null;
  return body?.message || body?.error || fallback;
}

export function DropEmailForm({
  storeId,
  defaultDropId = '',
  defaultDesignName = '',
  allowStoreId = false,
}: {
  storeId?: string;
  defaultDropId?: string;
  defaultDesignName?: string;
  allowStoreId?: boolean;
}) {
  const [sid, setSid] = useState(storeId || '');
  const [dropId, setDropId] = useState(defaultDropId);
  const [designName, setDesignName] = useState(defaultDesignName);
  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<SendResult | null>(null);

  async function run(dryRun: boolean) {
    const store = (storeId || sid).trim();
    const drop = dropId.trim();
    if (!store) {
      toast.error('Enter a store id.');
      return;
    }
    if (!drop) {
      toast.error('Enter a drop / campaign id.');
      return;
    }
    if (!dryRun && !confirm(`Email existing buyers for ${store} about “${designName.trim() || drop}”? This is not a cron — it sends now.`)) {
      return;
    }
    setBusy(dryRun ? 'preview' : 'send');
    try {
      const path = storeId
        ? `/api/team-stores/${encodeURIComponent(storeId)}/drop-email`
        : '/api/drop-email';
      const r = await post<SendResult>(path, {
        storeId: store,
        dropId: drop,
        designName: designName.trim() || undefined,
        dryRun,
      });
      setPreview(r);
      if (dryRun) toast.success(`Preview: ${r.buyers?.length ?? r.sent ?? 0} buyer(s) would be mailed`);
      else toast.success(`Sent ${r.sent ?? 0} · skipped ${r.skipped ?? 0} · failed ${r.failed ?? 0}`);
    } catch (e) {
      toast.error(errMsg(e, dryRun ? 'Preview failed' : 'Send failed'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
      <h3 className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold tracking-tight">
        <Mail className="h-3.5 w-3.5 text-subtle" aria-hidden /> Email existing buyers
      </h3>
      <p className="mb-3 text-[11.5px] text-muted">
        One store, one drop. Preview first. Not a daily cron — only when something dropped.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {allowStoreId && (
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Store id</label>
            <Input value={sid} onChange={(e) => setSid(e.target.value)} placeholder="ts_lincoln_fb" />
          </div>
        )}
        <div>
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Drop / campaign id</label>
          <Input value={dropId} onChange={(e) => setDropId(e.target.value)} placeholder="veterans-day-2026" />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">What dropped</label>
          <Input value={designName} onChange={(e) => setDesignName(e.target.value)} placeholder="Veterans Day sleeve" />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => void run(true)}>
          {busy === 'preview' ? 'Previewing…' : 'Preview'}
        </Button>
        <Button size="sm" disabled={!!busy} onClick={() => void run(false)}>
          {busy === 'send' ? 'Sending…' : 'Send drop email'}
        </Button>
      </div>
      {preview && (
        <p className="mt-3 text-[11.5px] text-muted">
          {preview.buyerSource ? `${preview.buyerSource} · ` : ''}
          {preview.buyers?.length != null ? `${preview.buyers.length} on the list` : `${preview.sent ?? 0} sent`}
          {preview.skipped != null ? ` · ${preview.skipped} skipped` : ''}
          {preview.failed != null ? ` · ${preview.failed} failed` : ''}
        </p>
      )}
    </div>
  );
}
